import { prettyQuarter } from '@/lib/format';

interface RegionPoint {
  quarter: string;
  subscribers: number;
  revenue: number;
}

/** One quarter's revenue growth on the previous, split into what drove it (percentage points). */
export type RevenueGrowthPoint = {
  quarter: string;
  label: string;
  /** Revenue growth, % — the sum of the three parts. */
  total: number;
  /** More (or fewer) subscribers. */
  subscribers: number;
  /** ARPU change within each region, at last quarter's regional mix — like-for-like price. */
  price: number;
  /** ARPU change from subscribers shifting between cheaper and dearer regions. */
  mix: number;
};

/**
 * Splits each quarter's revenue growth into subscribers, like-for-like price
 * and regional mix.
 *
 * Revenue R = S × A (subscribers × blended ARPU), and blended ARPU is each
 * region's ARPU weighted by its share of subscribers, A = Σ wᵣ·aᵣ. So a fall
 * in blended ARPU can be no price change at all — just cheaper regions growing
 * faster. Holding last quarter's weights gives the price-only ARPU,
 * A′ = Σ wᵣ(t−1)·aᵣ(t); the rest of the ARPU move is mix.
 *
 *   ln(R₁/R₀) = ln(S₁/S₀) + ln(A′/A₀) + ln(A₁/A′)
 *               subscribers   price        mix
 *
 * The log terms add up exactly; they're scaled to the actual % growth so the
 * stacked bar ends on the real figure. With one region there is no mix.
 */
export function revenueGrowthBreakdown(
  byRegion: Partial<Record<string, RegionPoint[]>>,
  regions: string[],
): RevenueGrowthPoint[] {
  const series = regions.map((r) => new Map((byRegion[r] ?? []).map((h) => [h.quarter, h])));
  if (series.some((s) => s.size === 0)) return [];
  // Quarters every region reports, oldest first.
  const quarters = [...series[0].keys()].filter((q) => series.every((s) => s.has(q))).sort();

  const out: RevenueGrowthPoint[] = [];
  for (let i = 1; i < quarters.length; i++) {
    const before = series.map((s) => s.get(quarters[i - 1])!);
    const now = series.map((s) => s.get(quarters[i])!);
    // A region with no subscribers on either side has no ARPU to compare.
    if (before.some((r) => r.subscribers <= 0) || now.some((r) => r.subscribers <= 0)) continue;

    const S0 = before.reduce((a, r) => a + r.subscribers, 0);
    const S1 = now.reduce((a, r) => a + r.subscribers, 0);
    const R0 = before.reduce((a, r) => a + r.revenue, 0);
    const R1 = now.reduce((a, r) => a + r.revenue, 0);
    if (R0 <= 0 || R1 <= 0) continue;

    const A0 = R0 / S0;
    const A1 = R1 / S1;
    const priceOnly = before.reduce((a, r, k) => a + (r.subscribers / S0) * (now[k].revenue / now[k].subscribers), 0);

    const lnS = Math.log(S1 / S0);
    const lnP = Math.log(priceOnly / A0);
    const lnM = Math.log(A1 / priceOnly);
    const lnR = lnS + lnP + lnM;
    const growth = (R1 / R0 - 1) * 100;
    const scale = Math.abs(lnR) > 1e-12 ? growth / lnR : 100;

    out.push({
      quarter: quarters[i],
      label: prettyQuarter(quarters[i]),
      total: growth,
      subscribers: lnS * scale,
      price: lnP * scale,
      mix: lnM * scale,
    });
  }
  return out;
}
